import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Alert, Spinner, Table, InputGroup } from 'react-bootstrap';
import { FaSave, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import './ServicesManagement.scss';

const ServicesManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [servicesData, setServicesData] = useState({
    header: 'Our Services',
    paragraph: 'We provide comprehensive real estate services to meet your needs',
    services: []
  });

  // List of common FontAwesome icons for the dropdown
  const commonIcons = [
    { name: 'faHome', label: 'Home' },
    { name: 'faBuilding', label: 'Building' },
    { name: 'faHandshake', label: 'Handshake' },
    { name: 'faTools', label: 'Tools' },
    { name: 'faKey', label: 'Key' },
    { name: 'faSearch', label: 'Search' },
    { name: 'faMoneyBillWave', label: 'Money' },
    { name: 'faChartLine', label: 'Chart' },
    { name: 'faMapMarkerAlt', label: 'Location' },
    { name: 'faPhone', label: 'Phone' },
    { name: 'faEnvelope', label: 'Email' },
    { name: 'faCalendarAlt', label: 'Calendar' },
    { name: 'faUserTie', label: 'User Tie' },
    { name: 'faUsers', label: 'Users' },
    { name: 'faUserShield', label: 'User Shield' },
    { name: 'faShieldAlt', label: 'Shield' },
    { name: 'faLock', label: 'Lock' },
    { name: 'faLightbulb', label: 'Lightbulb' },
    { name: 'faGem', label: 'Gem' },
    { name: 'faStar', label: 'Star' },
    { name: 'faAward', label: 'Award' },
    { name: 'faTrophy', label: 'Trophy' },
    { name: 'faThumbsUp', label: 'Thumbs Up' },
    { name: 'faHeart', label: 'Heart' },
    { name: 'faComments', label: 'Comments' },
    { name: 'faBriefcase', label: 'Briefcase' },
    { name: 'faGlobe', label: 'Globe' },
    { name: 'faLeaf', label: 'Leaf' },
    { name: 'faClock', label: 'Clock' },
    { name: 'faBullseye', label: 'Target' }
  ];

  useEffect(() => {
    fetchServicesData();
  }, []);

  const fetchServicesData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/services');
      
      if (response.data.status === 'success') {
        const { services } = response.data.data;
        
        // Find the first service with section_header and section_paragraph
        const sectionData = services.find(service => service.section_header || service.section_paragraph) || {};
        
        setServicesData({
          header: sectionData.section_header || 'Our Services',
          paragraph: sectionData.section_paragraph || 'We provide comprehensive real estate services to meet your needs',
          services: services.filter(service => service.title) // Filter out the section data if it's included in the services array
        });
      }
    } catch (error) {
      console.error('Error fetching services data:', error);
      setError('Failed to load services data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSectionData = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await axios.post('/api/services/section', {
        header: servicesData.header,
        paragraph: servicesData.paragraph
      });
      
      if (response.data.status === 'success') {
        setSuccess('Services section updated successfully!');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error saving services section data:', error);
      setError(error.response?.data?.message || 'Failed to save services section');
    } finally {
      setLoading(false);
    }
  };

  const handleAddService = () => {
    const newServices = [...servicesData.services, {
      title: 'New Service',
      icon: 'faHome',
      description: 'Description of this service',
      link: '/properties'
    }];
    setServicesData({...servicesData, services: newServices});
  };

  const handleSaveService = async (service, index) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      let response;
      
      if (service.id) {
        // Update existing service
        response = await axios.put(`/api/services/item/${service.id}`, {
          title: service.title,
          icon: service.icon,
          description: service.description,
          link: service.link
        });
      } else {
        // Add new service
        response = await axios.post('/api/services/item', {
          title: service.title,
          icon: service.icon,
          description: service.description,
          link: service.link
        });
        
        // Update the service in our state with the returned ID
        if (response.data.status === 'success') {
          const updatedServices = [...servicesData.services];
          updatedServices[index] = response.data.data;
          setServicesData({...servicesData, services: updatedServices});
        }
      }
      
      if (response.data.status === 'success') {
        setSuccess('Service saved successfully!');
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      }
    } catch (error) {
      console.error('Error saving service:', error);
      setError(error.response?.data?.message || 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteService = async (id, index) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (id) {
        // Delete from server if it has an ID
        const response = await axios.delete(`/api/services/item/${id}`);
        
        if (response.data.status === 'success') {
          // Remove from our state
          const updatedServices = servicesData.services.filter((_, i) => i !== index);
          setServicesData({...servicesData, services: updatedServices});
          
          setSuccess('Service deleted successfully!');
          
          // Clear success message after 5 seconds
          setTimeout(() => {
            setSuccess(null);
          }, 5000);
        }
      } else {
        // Just remove from state if it doesn't have an ID yet
        const updatedServices = servicesData.services.filter((_, i) => i !== index);
        setServicesData({...servicesData, services: updatedServices});
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      setError(error.response?.data?.message || 'Failed to delete service');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveService = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Check if the new index is valid
    if (newIndex < 0 || newIndex >= servicesData.services.length) return;
    
    // Create a copy of the services array
    const updatedServices = [...servicesData.services];
    
    // Swap the services
    [updatedServices[index], updatedServices[newIndex]] = [updatedServices[newIndex], updatedServices[index]];
    
    // Update the state
    setServicesData({...servicesData, services: updatedServices});
    
    // If the services have IDs, update the order on the server
    if (updatedServices.every(service => service.id)) {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.post('/api/services/order', {
          services: updatedServices.map((service, idx) => ({
            id: service.id,
            display_order: idx + 1
          }))
        });
        
        if (response.data.status !== 'success') {
          // If the update fails, revert the state
          const revertedServices = [...servicesData.services];
          setServicesData({...servicesData, services: revertedServices});
          setError('Failed to update service order');
        }
      } catch (error) {
        console.error('Error updating service order:', error);
        // Revert the state on error
        const revertedServices = [...servicesData.services];
        setServicesData({...servicesData, services: revertedServices});
        setError(error.response?.data?.message || 'Failed to update service order');
      } finally {
        setLoading(false);
      }
    }
  };

  // Helper function to get the icon object from its name
  const getIconByName = (iconName) => {
    return solidIcons[iconName];
  };

  const renderSectionForm = () => (
    <Card className="content-card mt-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Card.Title>Services Section</Card.Title>
          <Button 
            variant="primary" 
            onClick={handleSaveSectionData}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
        
        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-4">
            {success}
          </Alert>
        )}
        
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-4">
                <Form.Label>Header</Form.Label>
                <Form.Control
                  type="text"
                  value={servicesData.header}
                  onChange={(e) => setServicesData({ ...servicesData, header: e.target.value })}
                  placeholder="Enter header text"
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Paragraph</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={servicesData.paragraph}
                  onChange={(e) => setServicesData({ ...servicesData, paragraph: e.target.value })}
                  placeholder="Enter paragraph text"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Card.Body>
    </Card>
  );

  const renderServicesEditor = () => (
    <Card className="content-card mt-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Card.Title>Services</Card.Title>
          <Button 
            variant="success" 
            onClick={handleAddService}
            disabled={loading}
          >
            <FaPlus className="me-2" />
            Add Service
          </Button>
        </div>
        
        {servicesData.services && servicesData.services.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th>Title</th>
                <th>Icon</th>
                <th>Description</th>
                <th>Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {servicesData.services.map((service, index) => (
                <tr key={service.id || `new-${index}`}>
                  <td>
                    <Form.Control
                      type="text"
                      value={service.title || ''}
                      onChange={(e) => {
                        const updatedServices = [...servicesData.services];
                        updatedServices[index] = {...updatedServices[index], title: e.target.value};
                        setServicesData({...servicesData, services: updatedServices});
                      }}
                      placeholder="Service Title"
                    />
                  </td>
                  <td>
                    <InputGroup>
                      <InputGroup.Text>
                        {service.icon && (
                          <FontAwesomeIcon 
                            icon={getIconByName(service.icon)} 
                            style={{ width: '16px', height: '16px' }} 
                          />
                        )}
                      </InputGroup.Text>
                      <Form.Select
                        value={service.icon || ''}
                        onChange={(e) => {
                          const updatedServices = [...servicesData.services];
                          updatedServices[index] = {...updatedServices[index], icon: e.target.value};
                          setServicesData({...servicesData, services: updatedServices});
                        }}
                      >
                        <option value="">Select an icon</option>
                        {commonIcons.map((icon) => (
                          <option key={icon.name} value={icon.name}>
                            {icon.label}
                          </option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </td>
                  <td>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={service.description || ''}
                      onChange={(e) => {
                        const updatedServices = [...servicesData.services];
                        updatedServices[index] = {...updatedServices[index], description: e.target.value};
                        setServicesData({...servicesData, services: updatedServices});
                      }}
                      placeholder="Service Description"
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      value={service.link || ''}
                      onChange={(e) => {
                        const updatedServices = [...servicesData.services];
                        updatedServices[index] = {...updatedServices[index], link: e.target.value};
                        setServicesData({...servicesData, services: updatedServices});
                      }}
                      placeholder="/properties"
                    />
                  </td>
                  <td>
                    <div className="d-flex">
                      <Button
                        variant="primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleSaveService(service, index)}
                        disabled={loading}
                      >
                        <FaSave />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="me-2"
                        onClick={() => handleDeleteService(service.id, index)}
                        disabled={loading}
                      >
                        <FaTrash />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleMoveService(index, 'up')}
                        disabled={index === 0 || loading}
                      >
                        <FaArrowUp />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleMoveService(index, 'down')}
                        disabled={index === servicesData.services.length - 1 || loading}
                      >
                        <FaArrowDown />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Alert variant="info">
            No services added yet. Click the "Add Service" button to add your first service.
          </Alert>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <div className="services-management">
      {renderSectionForm()}
      {renderServicesEditor()}
    </div>
  );
};

export default ServicesManagement;
